import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type Step =
  | 'loading'
  | 'error'
  | 'prescription_type'
  | 'submission_method'
  | 'upload'
  | 'manual'
  | 'email_later'
  | 'lens_group'
  | 'lens'
  | 'lens_options'
  | 'addons'
  | 'review';

type CardKind = 'prescription' | 'submission' | 'group' | 'lens' | 'option' | 'addon';

interface LensBirdConfig {
  version?: string;
  restUrl?: string;
  nonce?: string;
  preloadedContext?: RuntimeContextPayload;
  lensIndexIcons?: Record<string, string>;
  prescriptionTypeIcons?: Record<string, string>;
  strings?: Record<string, string>;
}

interface SelectableItem {
  id?: string;
  code?: string;
  name?: string;
  label?: string;
  description?: string;
  active?: boolean;
  sort?: number;
  required?: boolean;
  requires_submission?: boolean;
  price_delta?: number | string;
  prescription_types?: string[];
}

interface OptionGroup extends SelectableItem {
  options?: SelectableItem[];
}

interface LensItem extends SelectableItem {
  option_groups?: OptionGroup[];
  addons?: SelectableItem[];
}

interface LensGroup extends SelectableItem {
  lenses?: LensItem[];
}

interface FlowContext {
  key?: string;
  version?: number;
  prescription_types?: SelectableItem[];
  submission_methods?: SelectableItem[];
  lens_groups?: LensGroup[];
}

interface ProductContext {
  id: number;
  name: string;
  sku?: string;
  price: number;
  price_html?: string;
}

interface CurrencyContext {
  code?: string;
  symbol?: string;
  price_format?: string;
  decimals?: number;
  decimal_separator?: string;
  thousand_separator?: string;
}

interface UploadRules {
  max_file_size_mb: number;
  allowed_mimes: string[];
}

interface RuntimeContextPayload {
  product?: ProductContext;
  flow?: FlowContext;
  locale?: { dictionary?: Record<string, string> };
  currency?: CurrencyContext;
  upload_rules?: UploadRules;
}

interface ManualValues {
  od_sphere: string;
  od_cylinder: string;
  od_axis: string;
  od_add: string;
  os_sphere: string;
  os_cylinder: string;
  os_axis: string;
  os_add: string;
  pd_mode: string;
  pd: string;
  pd_right: string;
  pd_left: string;
  add: string;
  reading_power: string;
}

interface Selections {
  prescriptionTypeId: string;
  submissionMethodId: string;
  uploadToken: string;
  uploadFileName: string;
  uploadSize: number;
  manualValues: ManualValues;
  emailLaterConfirmed: boolean;
  lensGroupId: string;
  lensId: string;
  optionSelections: Record<string, string>;
  addonIds: string[];
  orderNote: string;
}

interface RuntimeState {
  isOpen: boolean;
  productId: number;
  product: ProductContext | null;
  flow: FlowContext | null;
  locale: Record<string, string>;
  currency: CurrencyContext | null;
  uploadRules: UploadRules;
  currentStep: Step;
  stepHistory: Step[];
  errorMessage: string;
  validationError: string;
  isLoadingContext: boolean;
  isUploading: boolean;
  isAddingToCart: boolean;
  uploadError: string;
  uploadMimeType: string;
  uploadFileUrl: string;
  selections: Selections;
}

interface StepMeta {
  eyebrow: string;
  title: string;
  lead: string;
  cta: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface AppProps {
  launcher: HTMLElement;
}

declare global {
  interface Window {
    LensBirdConfig?: LensBirdConfig;
  }
}

function defaultManualValues(): ManualValues {
  return {
    od_sphere: '',
    od_cylinder: '',
    od_axis: '',
    od_add: '',
    os_sphere: '',
    os_cylinder: '',
    os_axis: '',
    os_add: '',
    pd_mode: 'single',
    pd: '',
    pd_right: '',
    pd_left: '',
    add: '',
    reading_power: '',
  };
}

function defaultManualValuesForManualEntry(prescriptionTypeId?: string): ManualValues {
  const values = {
    ...defaultManualValues(),
    od_sphere: '0.00',
    od_cylinder: '0.00',
    os_sphere: '0.00',
    os_cylinder: '0.00',
  };

  if (isProgressivePrescriptionType(prescriptionTypeId)) {
    values.od_add = '0.00';
    values.os_add = '0.00';
  }

  return values;
}

function createSelections(): Selections {
  return {
    prescriptionTypeId: '',
    submissionMethodId: '',
    uploadToken: '',
    uploadFileName: '',
    uploadSize: 0,
    manualValues: defaultManualValues(),
    emailLaterConfirmed: false,
    lensGroupId: '',
    lensId: '',
    optionSelections: {},
    addonIds: [],
    orderNote: '',
  };
}

function launcherProductId(launcher: HTMLElement): number {
  return Number.parseInt(launcher.dataset.productId ?? '0', 10) || 0;
}

function launcherProductPrice(launcher: HTMLElement): number {
  return Number.parseFloat(launcher.dataset.productPrice ?? '0') || 0;
}

function launcherProductName(launcher: HTMLElement): string {
  return launcher.dataset.productName ?? '';
}

function launcherProductImage(launcher: HTMLElement): string {
  return launcher.dataset.productImage ?? '';
}

function preloadedContextForLauncher(launcher: HTMLElement): RuntimeContextPayload | null {
  const payload = getConfig().preloadedContext;

  if (!payload || !payload.product || !payload.flow) {
    return null;
  }

  const payloadProductId = Number(payload.product.id ?? 0);
  const launcherProduct = launcherProductId(launcher);

  if (payloadProductId > 0 && launcherProduct > 0 && payloadProductId !== launcherProduct) {
    return null;
  }

  return payload;
}

function createInitialState(launcher: HTMLElement, isOpen: boolean): RuntimeState {
  const preloadedContext = isOpen ? preloadedContextForLauncher(launcher) : null;

  return {
    isOpen,
    productId: launcherProductId(launcher),
    product: preloadedContext?.product ?? null,
    flow: preloadedContext?.flow ?? null,
    locale: preloadedContext?.locale?.dictionary ?? {},
    currency: preloadedContext?.currency ?? null,
    uploadRules: preloadedContext?.upload_rules ?? {
      max_file_size_mb: 20,
      allowed_mimes: [],
    },
    currentStep: isOpen && preloadedContext ? 'prescription_type' : 'loading',
    stepHistory: [],
    errorMessage: '',
    validationError: '',
    isLoadingContext: isOpen && !preloadedContext,
    isUploading: false,
    isAddingToCart: false,
    uploadError: '',
    uploadMimeType: '',
    uploadFileUrl: '',
    selections: createSelections(),
  };
}

function getConfig(): LensBirdConfig {
  return window.LensBirdConfig ?? {};
}

function decodeHtmlEntities(value: string): string {
  if (!value || typeof document === 'undefined') {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function asNumber(value: number | string | undefined): number {
  return typeof value === 'number' ? value : Number.parseFloat(value ?? '0') || 0;
}

function itemId(item?: SelectableItem | null): string {
  return String(item?.id ?? item?.code ?? '');
}

function itemLabel(item?: SelectableItem | null): string {
  return String(item?.name ?? item?.label ?? itemId(item));
}

function normalizeItems<T extends SelectableItem>(items?: T[] | null): T[] {
  return (items ?? [])
    .filter((item): item is T => Boolean(item) && item.active !== false)
    .slice()
    .sort((left, right) => Number(left.sort ?? 0) - Number(right.sort ?? 0));
}

function findById<T extends SelectableItem>(items: T[] | undefined | null, targetId: string): T | null {
  if (!targetId) {
    return null;
  }

  return normalizeItems(items).find((item) => itemId(item) === targetId) ?? null;
}

function itemSupportsPrescription(item: SelectableItem | null | undefined, prescriptionTypeId: string): boolean {
  if (!item) {
    return false;
  }

  const supported = Array.isArray(item.prescription_types)
    ? item.prescription_types.map((value) => String(value).trim()).filter(Boolean)
    : [];

  if (!supported.length || !prescriptionTypeId) {
    return true;
  }

  return supported.includes(prescriptionTypeId);
}

function filterByPrescription<T extends SelectableItem>(items: T[], prescriptionTypeId: string): T[] {
  return items.filter((item) => itemSupportsPrescription(item, prescriptionTypeId));
}

function splitDescription(description?: string): string[] {
  return String(description ?? '')
    .split('\n')
    .map((line) => line.replace(/^[\s*-]+/, '').trim())
    .filter(Boolean);
}

function hasAxisRequirement(value: string): boolean {
  const normalized = value.trim();

  return Boolean(normalized) && !['0', '0.0', '0.00', '+0.00', '-0.00'].includes(normalized);
}

function isReadingPrescriptionType(prescriptionTypeId: string): boolean {
  return prescriptionTypeId === 'reading';
}

function isProgressivePrescriptionType(prescriptionTypeId: string): boolean {
  return prescriptionTypeId === 'progressive';
}

function extractLensBadge(label: string): string {
  const match = label.match(/(\d\.\d{1,2})/);
  return match ? match[1] : 'RX';
}

function lensIndexImageUrl(label: string, icons?: Record<string, string>): string {
  if (!icons) {
    return '';
  }

  const match = label.match(/(\d\.\d{1,2})/);
  if (!match) {
    return '';
  }

  const index = match[1];

  return icons[index] ?? '';
}

function prescriptionTypeImageUrl(item: SelectableItem, icons?: Record<string, string>): string {
  if (!icons) {
    return '';
  }

  return icons[itemId(item)] ?? '';
}

function formatUploadFormats(allowedMimes: string[]): string {
  const mimeMap: Record<string, string[]> = {
    'image/jpeg': ['JPG', 'JPEG'],
    'image/png': ['PNG'],
    'application/pdf': ['PDF'],
    'image/webp': ['WEBP'],
    'image/heic': ['HEIC'],
    'image/heif': ['HEIF'],
  };
  const labels: string[] = [];
  const seen = new Set<string>();

  allowedMimes.forEach((mime) => {
    (mimeMap[mime] ?? [mime.toUpperCase()]).forEach((label) => {
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    });
  });

  return labels.join(', ');
}

function isImageMimeType(mimeType: string | undefined | null): boolean {
  return String(mimeType ?? '')
    .trim()
    .toLowerCase()
    .startsWith('image/');
}

function looksLikeImageUpload(value: string | undefined | null): boolean {
  return /\.(avif|bmp|gif|heic|heif|jpe?g|png|webp)(?:[?#].*)?$/i.test(String(value ?? '').trim());
}

function shouldRenderUploadImage(mimeType: string | undefined | null, fileUrl: string, fileName: string): boolean {
  if (!fileUrl) {
    return false;
  }

  return isImageMimeType(mimeType) || looksLikeImageUpload(fileUrl) || looksLikeImageUpload(fileName);
}

function hasMeaningfulManualValues(values: ManualValues): boolean {
  return [
    values.od_sphere,
    values.od_cylinder,
    values.od_axis,
    values.od_add,
    values.os_sphere,
    values.os_cylinder,
    values.os_axis,
    values.os_add,
    values.pd,
    values.pd_right,
    values.pd_left,
    values.add,
    values.reading_power,
  ].some((value) => value.trim() !== '');
}

function isLensPromoLine(line: string): boolean {
  return line.trim().toLowerCase() === 'best value';
}

function isHighlightedLensFeature(lensLabel: string, line: string): boolean {
  const normalizedLine = line.trim().toLowerCase();
  const normalizedLens = lensLabel.trim().toLowerCase();

  if (
    normalizedLine.includes('thinner than') ||
    normalizedLine.includes('for moderate prescription') ||
    normalizedLine.includes('for strong prescription') ||
    normalizedLine.includes('uv-protective') ||
    normalizedLine.includes('impact-resistant')
  ) {
    return true;
  }

  if (
    normalizedLens.includes('popular') &&
    (normalizedLine.includes('anti-scratch') || normalizedLine.includes('anti-reflective'))
  ) {
    return true;
  }

  return false;
}

function choiceBadge(kind: CardKind, item: SelectableItem): string {
  const id = itemId(item);

  if (kind === 'prescription') {
    if (id === 'non_prescription') {
      return 'NP';
    }

    if (id === 'single_vision') {
      return 'SV';
    }

    if (id === 'reading') {
      return 'RD';
    }

    if (id === 'progressive') {
      return 'PG';
    }
  }

  if (kind === 'submission') {
    if (id === 'upload') {
      return 'UP';
    }

    if (id === 'manual') {
      return 'RX';
    }

    if (id === 'email_later') {
      return 'EM';
    }
  }

  if (kind === 'lens') {
    return extractLensBadge(itemLabel(item));
  }

  if (kind === 'group') {
    return 'LG';
  }

  if (kind === 'option') {
    return 'OP';
  }

  return '+';
}

function prescriptionChoiceHint(item: SelectableItem, translate: (key: string, fallback: string) => string): string {
  const id = itemId(item);

  if (id === 'non_prescription') {
    return translate('choice.prescription.non_prescription_hint', 'No prescription');
  }

  if (id === 'single_vision') {
    return translate('choice.prescription.single_vision_hint', 'One distance');
  }

  if (id === 'reading') {
    return translate('choice.prescription.reading_hint', 'Reading only');
  }

  if (id === 'progressive') {
    return translate('choice.prescription.progressive_hint', 'Near + far');
  }

  return '';
}

function buildNumericOptions(
  start: number,
  end: number,
  step: number,
  options?: { precision?: number; signed?: boolean; includeBlank?: boolean; blankLabel?: string },
): SelectOption[] {
  const precision = options?.precision ?? 2;
  const includeBlank = options?.includeBlank ?? true;
  const signed = options?.signed ?? false;
  const blankLabel = options?.blankLabel ?? 'Choose';
  const values: SelectOption[] = [];
  const delta = start <= end ? Math.abs(step) : -Math.abs(step);

  if (includeBlank) {
    values.push({ value: '', label: blankLabel });
  }

  for (
    let current = start;
    delta > 0 ? current <= end + 0.00001 : current >= end - 0.00001;
    current += delta
  ) {
    const normalized = Number(current.toFixed(precision));
    const value = normalized.toFixed(precision);
    const label = signed && normalized > 0 ? `+${value}` : value;
    values.push({ value, label });
  }

  return values;
}

function buildIntegerOptions(start: number, end: number, blankLabel = '0'): SelectOption[] {
  const values: SelectOption[] = [{ value: '', label: blankLabel }];

  for (let current = start; current <= end; current += 1) {
    values.push({ value: String(current), label: String(current) });
  }

  return values;
}

function transitionState(
  current: RuntimeState,
  nextStep: Step,
  transform?: (value: RuntimeState) => RuntimeState,
): RuntimeState {
  const next = transform ? transform(current) : current;
  const shouldPushHistory =
    current.currentStep !== 'loading' && current.currentStep !== nextStep && current.currentStep !== 'error';

  return {
    ...next,
    stepHistory: shouldPushHistory ? [...current.stepHistory, current.currentStep] : current.stepHistory,
    currentStep: nextStep,
    validationError: '',
  };
}

const SPH_OPTIONS = buildNumericOptions(-10, 6, 0.25, { signed: true, includeBlank: false });
const CYL_OPTIONS = buildNumericOptions(-6, 6, 0.25, { signed: true, includeBlank: false });
const AXIS_OPTIONS = buildIntegerOptions(0, 180, '0');
const PD_OPTIONS = buildNumericOptions(35, 79, 0.5, { blankLabel: 'Choose' });
const DUAL_PD_OPTIONS = buildNumericOptions(17.5, 40, 0.5, { blankLabel: 'Choose' });
const ADD_OPTIONS = buildNumericOptions(0, 3.5, 0.25, { signed: true, includeBlank: false });
const READING_DIOPTER_OPTIONS = buildNumericOptions(0.25, 3, 0.25, { signed: true, blankLabel: '0.00' });
const AUTO_ADVANCE_STEPS: Step[] = ['prescription_type', 'submission_method', 'lens_group', 'lens'];

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="lens-bird-review__row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  className,
  hideLabel,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  className?: string;
  hideLabel?: boolean;
}) {
  return (
    <label className={className ? `lens-bird-field ${className}` : 'lens-bird-field'}>
      <span className={hideLabel ? 'lens-bird-field__label lens-bird-field__label--hidden' : 'lens-bird-field__label'}>{label}</span>
      <select
        aria-label={label}
        className="lens-bird-field__input lens-bird-field__select"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {options.map((option) => (
          <option key={`${label}-${option.value || 'blank'}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ChoiceCardState({
  selected,
  chooseLabel,
  selectedLabel,
}: {
  selected: boolean;
  chooseLabel: string;
  selectedLabel: string;
}) {
  return (
    <div className={selected ? 'lens-bird-choice-card__state is-selected' : 'lens-bird-choice-card__state'} aria-hidden="true">
      <span className={selected ? 'lens-bird-choice-card__indicator is-selected' : 'lens-bird-choice-card__indicator'}>
        {selected ? (
          <svg className="lens-bird-choice-card__indicator-icon" viewBox="0 0 16 16" fill="none">
            <path d="M4 8.4 6.6 11 12 5.6" />
          </svg>
        ) : null}
      </span>
      <span className="lens-bird-choice-card__state-label">{selected ? selectedLabel : chooseLabel}</span>
    </div>
  );
}

function SelectionCards({
  items,
  selectedId,
  onSelect,
  showPrice,
  formatPriceDelta,
  kind,
  chooseLabel,
  selectedLabel,
  getEyebrow,
}: {
  items: SelectableItem[];
  selectedId: string;
  onSelect: (itemId: string) => void;
  showPrice?: boolean;
  formatPriceDelta: (amount: number) => string;
  kind: CardKind;
  chooseLabel: string;
  selectedLabel: string;
  getEyebrow?: (item: SelectableItem) => string;
}) {
  const lensIndexIcons = getConfig().lensIndexIcons ?? {};
  const prescriptionTypeIcons = getConfig().prescriptionTypeIcons ?? {};

  return (
    <div className={kind === 'prescription' ? 'lens-bird-choice-grid lens-bird-choice-grid--prescription' : 'lens-bird-choice-grid'}>
      {normalizeItems(items).map((item) => {
        const currentId = itemId(item);
        const selected = currentId === selectedId;
        const lines = splitDescription(item.description);
        const promoLine = kind === 'lens' && lines.length > 1 && isLensPromoLine(lines[0]) ? lines[0] : '';
        const detailLines = promoLine ? lines.slice(1) : lines;
        const eyebrow = getEyebrow ? getEyebrow(item) : '';
        const mediaImageUrl =
          kind === 'lens'
            ? lensIndexImageUrl(itemLabel(item), lensIndexIcons)
            : kind === 'prescription'
              ? prescriptionTypeImageUrl(item, prescriptionTypeIcons)
              : '';

        return (
          <button
            key={currentId}
            type="button"
            className={selected ? 'lens-bird-choice-card is-selected' : 'lens-bird-choice-card'}
            data-kind={kind}
            aria-pressed={selected}
            onClick={() => onSelect(currentId)}
          >
            {mediaImageUrl ? (
              <div className="lens-bird-choice-card__media">
                <img className="lens-bird-choice-card__media-image" src={mediaImageUrl} alt={itemLabel(item)} />
              </div>
            ) : (
              <div className="lens-bird-choice-card__badge">{choiceBadge(kind, item)}</div>
            )}
            <div className="lens-bird-choice-card__content">
              {eyebrow ? <div className="lens-bird-choice-card__eyebrow">{eyebrow}</div> : null}
              <div className="lens-bird-choice-card__topline">
                <div className="lens-bird-choice-card__title">{itemLabel(item)}</div>
                {showPrice ? <div className="lens-bird-choice-card__price">{formatPriceDelta(asNumber(item.price_delta))}</div> : null}
              </div>
              {promoLine ? <div className="lens-bird-choice-card__promo">{promoLine}</div> : null}
              {kind === 'lens' && detailLines.length ? (
                <ul className="lens-bird-choice-card__list lens-bird-choice-card__list--lens">
                  {detailLines.map((line) => (
                    <li
                      key={`${currentId}-${line}`}
                      className={
                        isHighlightedLensFeature(itemLabel(item), line)
                          ? 'lens-bird-choice-card__list-item lens-bird-choice-card__list-item--highlight'
                          : 'lens-bird-choice-card__list-item'
                      }
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              ) : null}
              {kind !== 'lens' && lines.length === 1 ? <div className="lens-bird-choice-card__desc">{lines[0]}</div> : null}
              {kind !== 'lens' && lines.length > 1 ? (
                <ul className="lens-bird-choice-card__list">
                  {lines.map((line) => (
                    <li key={`${currentId}-${line}`}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <ChoiceCardState selected={selected} chooseLabel={chooseLabel} selectedLabel={selectedLabel} />
          </button>
        );
      })}
    </div>
  );
}

export function App({ launcher }: AppProps) {
  const [state, setState] = useState<RuntimeState>(() => createInitialState(launcher, false));
  const [isDesktopLayout, setIsDesktopLayout] = useState<boolean>(() => {
    return typeof window !== 'undefined' ? window.innerWidth >= 960 : true;
  });
  const [isUploadDragActive, setIsUploadDragActive] = useState<boolean>(false);

  const t = (key: string, fallback: string): string => {
    return state.locale[key] || fallback;
  };

  const productPrice = state.product?.price ?? launcherProductPrice(launcher);
  const productName = state.product?.name ?? launcherProductName(launcher);
  const productImage = launcherProductImage(launcher);
  const prescriptionTypes = normalizeItems(state.flow?.prescription_types);
  const submissionMethods = normalizeItems(state.flow?.submission_methods);
  const chooseLabel = t('choice.select', 'Select');
  const selectedLabel = t('choice.selected', 'Selected');
  const selectedPrescriptionType = findById(prescriptionTypes, state.selections.prescriptionTypeId);
  const selectedSubmissionMethod = findById(submissionMethods, state.selections.submissionMethodId);
  const lensGroups = filterByPrescription(normalizeItems(state.flow?.lens_groups), state.selections.prescriptionTypeId);
  const selectedLensGroup = findById(lensGroups, state.selections.lensGroupId) ?? (lensGroups.length === 1 ? lensGroups[0] : null);
  const lenses = normalizeItems(selectedLensGroup?.lenses);
  const selectedLens = findById(lenses, state.selections.lensId);
  const optionGroups = normalizeItems(selectedLens?.option_groups);
  const availableAddons = normalizeItems(selectedLens?.addons);

  useEffect(() => {
    const updateLayout = () => {
      setIsDesktopLayout(window.innerWidth >= 960);
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, []);

  useEffect(() => {
    const button = launcher.querySelector<HTMLButtonElement>('[data-lens-bird-open]');
    if (!button) {
      return undefined;
    }

    const openOverlay = (event: Event) => {
      event.preventDefault();
      setState(createInitialState(launcher, true));
    };

    button.addEventListener('click', openOverlay);

    return () => {
      button.removeEventListener('click', openOverlay);
    };
  }, [launcher]);

  useEffect(() => {
    document.documentElement.classList.toggle('lens-bird-scroll-lock', state.isOpen);
    document.body.classList.toggle('lens-bird-scroll-lock', state.isOpen);

    return () => {
      document.documentElement.classList.remove('lens-bird-scroll-lock');
      document.body.classList.remove('lens-bird-scroll-lock');
    };
  }, [state.isOpen]);

  useEffect(() => {
    if (!state.isOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setState(createInitialState(launcher, false));
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [launcher, state.isOpen]);

  useEffect(() => {
    if (!state.isOpen || !state.isLoadingContext) {
      return undefined;
    }

    let cancelled = false;
    const config = getConfig();

    fetch(`${config.restUrl ?? ''}context?product_id=${encodeURIComponent(String(state.productId))}`, {
      method: 'GET',
      headers: {
        'X-WP-Nonce': config.nonce ?? '',
      },
      credentials: 'same-origin',
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('invalid_context');
        }

        return response.json();
      })
      .then((payload: {
        product?: ProductContext;
        flow?: FlowContext;
        locale?: { dictionary?: Record<string, string> };
        currency?: CurrencyContext;
        upload_rules?: UploadRules;
      }) => {
        if (cancelled) {
          return;
        }

        setState((current) => ({
          ...current,
          isLoadingContext: false,
          product: payload.product ?? null,
          flow: payload.flow ?? null,
          locale: payload.locale?.dictionary ?? {},
          currency: payload.currency ?? null,
          uploadRules: payload.upload_rules ?? current.uploadRules,
          currentStep: 'prescription_type',
          errorMessage: '',
        }));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setState((current) => ({
          ...current,
          isLoadingContext: false,
          currentStep: 'error',
          errorMessage: t(
            'errors.invalid_context',
            config.strings?.failedContext ?? 'Unable to load lens configuration for this product.',
          ),
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [state.isLoadingContext, state.isOpen, state.productId]);

  const closeOverlay = () => {
    setState(createInitialState(launcher, false));
  };

  const goBack = () => {
    setState((current) => {
      if (!current.stepHistory.length || current.isUploading || current.isAddingToCart) {
        return current;
      }

      const nextHistory = current.stepHistory.slice();
      const previousStep = nextHistory.pop();
      if (!previousStep) {
        return current;
      }

      return {
        ...current,
        currentStep: previousStep,
        stepHistory: nextHistory,
        validationError: '',
      };
    });
  };

  const updateSelections = (recipe: (current: Selections) => Selections) => {
    setState((current) => ({
      ...current,
      selections: recipe(current.selections),
    }));
  };

  const getLensGroupsForPrescription = (prescriptionTypeId: string): LensGroup[] => {
    return filterByPrescription(normalizeItems(state.flow?.lens_groups), prescriptionTypeId);
  };

  const getLensEntryTransition = (prescriptionTypeId = state.selections.prescriptionTypeId): { nextStep: Step; lensGroupId: string } => {
    const availableLensGroups = getLensGroupsForPrescription(prescriptionTypeId);

    if (!availableLensGroups.length) {
      return { nextStep: 'review', lensGroupId: '' };
    }

    if (availableLensGroups.length === 1) {
      return { nextStep: 'lens', lensGroupId: itemId(availableLensGroups[0]) };
    }

    return { nextStep: 'lens_group', lensGroupId: '' };
  };

  const getPostLensStepForLens = (lens: LensItem | null): Step => {
    if (normalizeItems(lens?.option_groups).length) {
      return 'lens_options';
    }

    if (normalizeItems(lens?.addons).length) {
      return 'addons';
    }

    return 'review';
  };

  const selectPrescriptionType = (prescriptionTypeId: string) => {
    const prescriptionType = findById(prescriptionTypes, prescriptionTypeId);
    if (!prescriptionType) {
      return;
    }

    const requiresSubmission = Object.prototype.hasOwnProperty.call(prescriptionType, 'requires_submission')
      ? Boolean(prescriptionType.requires_submission)
      : prescriptionTypeId !== 'non_prescription';
    const lensEntry = getLensEntryTransition(prescriptionTypeId);

    setState((current) =>
      transitionState(
        {
          ...current,
          validationError: '',
          uploadError: '',
          isUploading: false,
          uploadMimeType: '',
          uploadFileUrl: '',
          selections: {
            ...createSelections(),
            prescriptionTypeId,
            lensGroupId: requiresSubmission ? '' : lensEntry.lensGroupId,
          },
        },
        requiresSubmission ? 'submission_method' : lensEntry.nextStep,
      ),
    );
  };

  const selectSubmissionMethod = (submissionMethodId: string) => {
    const lensEntry = getLensEntryTransition();
    const isEmailLater = submissionMethodId === 'email_later';

    setState((current) =>
      transitionState(
        {
          ...current,
          validationError: '',
          uploadError: '',
          isUploading: false,
          uploadMimeType: '',
          uploadFileUrl: '',
          selections: {
            ...current.selections,
            submissionMethodId,
            uploadToken: '',
            uploadFileName: '',
            uploadSize: 0,
            manualValues:
              submissionMethodId === 'manual' && !isReadingPrescriptionType(current.selections.prescriptionTypeId)
                ? defaultManualValuesForManualEntry(current.selections.prescriptionTypeId)
                : defaultManualValues(),
            emailLaterConfirmed: isEmailLater,
            lensGroupId: isEmailLater ? lensEntry.lensGroupId || current.selections.lensGroupId : current.selections.lensGroupId,
          },
        },
        isEmailLater ? lensEntry.nextStep : (submissionMethodId as Step),
      ),
    );
  };

  const selectLensGroup = (lensGroupId: string) => {
    setState((current) =>
      transitionState(
        {
          ...current,
          selections: {
            ...current.selections,
            lensGroupId,
            lensId: '',
            optionSelections: {},
            addonIds: [],
          },
        },
        'lens',
      ),
    );
  };

  const selectLens = (lensId: string) => {
    const lens = findById(lenses, lensId);
    const nextStep = getPostLensStepForLens(lens);

    setState((current) =>
      transitionState(
        {
          ...current,
          selections: {
            ...current.selections,
            lensId,
            optionSelections: {},
            addonIds: [],
          },
        },
        nextStep,
      ),
    );
  };

  const selectOption = (groupId: string, optionId: string) => {
    updateSelections((current) => ({
      ...current,
      optionSelections: {
        ...current.optionSelections,
        [groupId]: optionId,
      },
    }));
    setState((current) => ({ ...current, validationError: '' }));
  };

  const toggleAddon = (addonId: string) => {
    updateSelections((current) => {
      const addonIds = current.addonIds.includes(addonId)
        ? current.addonIds.filter((value) => value !== addonId)
        : [...current.addonIds, addonId];

      return {
        ...current,
        addonIds,
      };
    });
  };

  const updateManualValue = (name: keyof ManualValues, value: string) => {
    updateSelections((current) => ({
      ...current,
      manualValues: {
        ...current.manualValues,
        [name]: value,
      },
    }));
    setState((current) => ({ ...current, validationError: '' }));
  };

  const getSelectedOptionNames = (): string[] => {
    return optionGroups
      .map((group) => {
        const option = findById(group.options, state.selections.optionSelections[itemId(group)]);
        return option ? itemLabel(option) : '';
      })
      .filter(Boolean);
  };

  const getSelectedAddonNames = (): string[] => {
    return availableAddons
      .filter((addon) => state.selections.addonIds.includes(itemId(addon)))
      .map((addon) => itemLabel(addon));
  };

  const calculatePricing = () => {
    const optionsPrice = optionGroups.reduce((sum, group) => {
      const option = findById(group.options, state.selections.optionSelections[itemId(group)]);
      return sum + (option ? asNumber(option.price_delta) : 0);
    }, 0);

    const addonsPrice = availableAddons.reduce((sum, addon) => {
      return sum + (state.selections.addonIds.includes(itemId(addon)) ? asNumber(addon.price_delta) : 0);
    }, 0);

    const lensPrice = selectedLens ? asNumber(selectedLens.price_delta) : 0;
    const total = productPrice + lensPrice + optionsPrice + addonsPrice;

    return {
      base: productPrice,
      lens: lensPrice,
      options: optionsPrice,
      addons: addonsPrice,
      total,
    };
  };

  const formatMoney = (amount: number): string => {
    const currency = state.currency ?? {};
    const absoluteAmount = Math.abs(amount);
    const decimals = typeof currency.decimals === 'number' ? currency.decimals : 2;
    const decimalSeparator = currency.decimal_separator ?? '.';
    const thousandSeparator = currency.thousand_separator ?? ',';
    const symbol = decodeHtmlEntities(currency.symbol ?? '');
    const format = decodeHtmlEntities(currency.price_format ?? '%1$s%2$s');
    const fixed = absoluteAmount.toFixed(decimals).split('.');
    const integerPart = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    const decimalPart = fixed.length > 1 ? `${decimalSeparator}${fixed[1]}` : '';
    const formatted = `${integerPart}${decimalPart}`;
    const output = format.replace('%1$s', symbol).replace('%2$s', formatted);

    return amount < 0 ? `-${output}` : output;
  };

  const formatPriceDelta = (amount: number): string => {
    if (amount === 0) {
      return t('pricing.included', 'Included');
    }

    return `+${formatMoney(amount)}`;
  };

  const uploadAccept = state.uploadRules.allowed_mimes.join(',') || 'image/jpeg,image/png,application/pdf,image/webp,image/heic,image/heif';
  const uploadMaxSizeMb = state.uploadRules.max_file_size_mb || 20;

  const formatManualSummary = (): string => {
    const values = state.selections.manualValues;
    const parts: string[] = [];
    const odParts: string[] = [];
    const osParts: string[] = [];

    if (isReadingPrescriptionType(state.selections.prescriptionTypeId)) {
      if (values.reading_power) {
        parts.push(`Reading ${values.reading_power}`);
      }

      return parts.join(', ');
    }

    if (values.od_sphere) {
      odParts.push(`SPH ${values.od_sphere}`);
    }

    if (values.od_cylinder) {
      odParts.push(`CYL ${values.od_cylinder}`);
    }

    if (values.od_axis) {
      odParts.push(`AXIS ${values.od_axis}`);
    }

    if (values.os_sphere) {
      osParts.push(`SPH ${values.os_sphere}`);
    }

    if (values.os_cylinder) {
      osParts.push(`CYL ${values.os_cylinder}`);
    }

    if (values.os_axis) {
      osParts.push(`AXIS ${values.os_axis}`);
    }

    if (values.pd_mode === 'dual') {
      if (values.pd_right) {
        parts.push(`PD R ${values.pd_right}`);
      }

      if (values.pd_left) {
        parts.push(`PD L ${values.pd_left}`);
      }
    } else if (values.pd) {
      parts.push(`PD ${values.pd}`);
    }

    if (isProgressivePrescriptionType(state.selections.prescriptionTypeId)) {
      if (values.od_add) {
        odParts.push(`ADD ${values.od_add}`);
      }

      if (values.os_add) {
        osParts.push(`ADD ${values.os_add}`);
      }
    }

    if (odParts.length) {
      parts.unshift(`OD ${odParts.join(' / ')}`);
    }

    if (osParts.length) {
      parts.splice(odParts.length ? 1 : 0, 0, `OS ${osParts.join(' / ')}`);
    }

    return parts.join(', ');
  };

  const formatPrescriptionCell = (value: string): string => {
    return value.trim() ? value.trim() : '—';
  };

  const formatPrescriptionAxis = (value: string): string => {
    return value.trim() ? `${value.trim()}°` : '—';
  };

  const formatPrescriptionMillimeters = (value: string): string => {
    return value.trim() ? `${value.trim()} mm` : '—';
  };

  const renderManualPrescriptionPreview = (): React.ReactNode => {
    if (state.selections.submissionMethodId !== 'manual') {
      return null;
    }

    const values = state.selections.manualValues;

    if (isReadingPrescriptionType(state.selections.prescriptionTypeId)) {
      if (!values.reading_power.trim()) {
        return null;
      }

      return (
        <div className="lens-bird-rx-card lens-bird-rx-card--simple">
          <div className="lens-bird-rx-simple__label">{t('review.rx_reading_power', 'Reading Power')}</div>
          <div className="lens-bird-rx-simple__value">{formatPrescriptionCell(values.reading_power)}</div>
        </div>
      );
    }

    const pdValue =
      values.pd_mode === 'dual'
        ? [values.pd_right.trim() ? `${t('manual.field.pd_right', 'Right PD')} ${formatPrescriptionMillimeters(values.pd_right)}` : '', values.pd_left.trim() ? `${t('manual.field.pd_left', 'Left PD')} ${formatPrescriptionMillimeters(values.pd_left)}` : '']
            .filter(Boolean)
            .join(' / ')
        : formatPrescriptionMillimeters(values.pd);

    return (
      <div className="lens-bird-rx-card">
        <div className="lens-bird-rx-table">
          <div className="lens-bird-rx-table__row lens-bird-rx-table__row--head">
            <div className="lens-bird-rx-table__cell lens-bird-rx-table__cell--label" />
            <div className="lens-bird-rx-table__cell">{t('review.rx_sph', 'SPH')}</div>
            <div className="lens-bird-rx-table__cell">{t('review.rx_cyl', 'CYL')}</div>
            <div className="lens-bird-rx-table__cell">{t('review.rx_axis', 'Axis')}</div>
            <div className="lens-bird-rx-table__cell">{t('review.rx_add', 'ADD')}</div>
          </div>
          <div className="lens-bird-rx-table__row">
            <div className="lens-bird-rx-table__cell lens-bird-rx-table__cell--label">{t('review.rx_right', 'Right (OD)')}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.od_sphere)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.od_cylinder)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionAxis(values.od_axis)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.od_add)}</div>
          </div>
          <div className="lens-bird-rx-table__row">
            <div className="lens-bird-rx-table__cell lens-bird-rx-table__cell--label">{t('review.rx_left', 'Left (OS)')}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.os_sphere)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.os_cylinder)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionAxis(values.os_axis)}</div>
            <div className="lens-bird-rx-table__cell">{formatPrescriptionCell(values.os_add)}</div>
          </div>
        </div>
        <div className="lens-bird-rx-pd">
          <div className="lens-bird-rx-pd__label">{t('review.rx_pd', 'PD')}</div>
          <div className="lens-bird-rx-pd__value">{pdValue || '—'}</div>
        </div>
      </div>
    );
  };

  const renderUploadPreview = (): React.ReactNode => {
    if (!state.selections.uploadFileName) {
      return null;
    }

    if (shouldRenderUploadImage(state.uploadMimeType, state.uploadFileUrl, state.selections.uploadFileName)) {
      return (
        <div className="lens-bird-rx-upload">
          <div className="lens-bird-rx-upload__label">{t('review.upload_preview', 'Uploaded Prescription')}</div>
          <a href={state.uploadFileUrl} target="_blank" rel="noreferrer">
            <img className="lens-bird-rx-upload__image" src={state.uploadFileUrl} alt={state.selections.uploadFileName} />
          </a>
          <div className="lens-bird-rx-upload__name">{state.selections.uploadFileName}</div>
        </div>
      );
    }

    return (
      <div className="lens-bird-rx-upload lens-bird-rx-upload--file">
        <div className="lens-bird-rx-upload__label">{t('summary.uploaded_file_label', 'Uploaded File')}</div>
        <div className="lens-bird-rx-upload__name">{state.selections.uploadFileName}</div>
      </div>
    );
  };

  const validateManualValues = (): string[] => {
    const values = state.selections.manualValues;
    const errors: string[] = [];

    if (isReadingPrescriptionType(state.selections.prescriptionTypeId)) {
      if (!values.reading_power.trim()) {
        errors.push(t('errors.manual.reading_power_required', 'Reading strength is required.'));
      }

      return errors;
    }

    if (!values.od_sphere.trim()) {
      errors.push(t('errors.manual.od_sphere_required', 'OD Sphere is required.'));
    }

    if (!values.os_sphere.trim()) {
      errors.push(t('errors.manual.os_sphere_required', 'OS Sphere is required.'));
    }

    if (values.pd_mode === 'dual') {
      if (!values.pd_right.trim()) {
        errors.push(t('errors.manual.pd_right_required', 'Right PD is required.'));
      }

      if (!values.pd_left.trim()) {
        errors.push(t('errors.manual.pd_left_required', 'Left PD is required.'));
      }
    } else if (!values.pd.trim()) {
      errors.push(t('errors.manual.pd_required', 'PD is required.'));
    }

    if (hasAxisRequirement(values.od_cylinder) && !values.od_axis.trim()) {
      errors.push(t('errors.manual.od_axis_required', 'OD Axis is required when OD Cylinder is provided.'));
    }

    if (hasAxisRequirement(values.os_cylinder) && !values.os_axis.trim()) {
      errors.push(t('errors.manual.os_axis_required', 'OS Axis is required when OS Cylinder is provided.'));
    }

    if (isProgressivePrescriptionType(state.selections.prescriptionTypeId)) {
      if (!values.od_add.trim()) {
        errors.push(t('errors.manual.od_add_required', 'OD ADD is required for progressive lenses.'));
      }

      if (!values.os_add.trim()) {
        errors.push(t('errors.manual.os_add_required', 'OS ADD is required for progressive lenses.'));
      }
    }

    return errors;
  };

  const validateOptionSelections = (): string[] => {
    const errors: string[] = [];

    optionGroups.forEach((group) => {
      if (group.required && !state.selections.optionSelections[itemId(group)]) {
        errors.push(t('errors.option_required', 'Please complete the required lens options before continuing.'));
      }
    });

    return errors;
  };

  const canContinue = (): boolean => {
    switch (state.currentStep) {
      case 'prescription_type':
        return Boolean(state.selections.prescriptionTypeId);
      case 'submission_method':
        return Boolean(state.selections.submissionMethodId);
      case 'upload':
        return Boolean(state.selections.uploadToken) && !state.isUploading;
      case 'manual':
        return validateManualValues().length === 0;
      case 'email_later':
        return state.selections.emailLaterConfirmed;
      case 'lens_group':
        return Boolean(selectedLensGroup);
      case 'lens':
        return Boolean(state.selections.lensId);
      case 'lens_options':
        return validateOptionSelections().length === 0;
      case 'addons':
        return true;
      case 'review':
        return !state.isAddingToCart;
      case 'error':
        return true;
      default:
        return false;
    }
  };

  const moveToStep = (nextStep: Step, transform?: (current: RuntimeState) => RuntimeState) => {
    setState((current) => transitionState(current, nextStep, transform));
  };

  const getSelectedQuantity = (): number => {
    const cartForm = launcher.closest('form.cart');
    const quantityInput = cartForm?.querySelector<HTMLInputElement>('input.qty[name="quantity"], input[name="quantity"]');
    const quantity = Number.parseInt(quantityInput?.value ?? '1', 10);

    return quantity > 0 ? quantity : 1;
  };

  const buildAddToCartPayload = () => {
    let submissionMethod = state.selections.submissionMethodId;
    let emailLater = state.selections.emailLaterConfirmed;

    if (submissionMethod === 'email_later') {
      if (hasMeaningfulManualValues(state.selections.manualValues)) {
        submissionMethod = 'manual';
        emailLater = false;
      } else if (state.selections.uploadToken) {
        submissionMethod = 'upload';
        emailLater = false;
      }
    }

    return {
      product_id: state.productId,
      quantity: getSelectedQuantity(),
      prescription_type: state.selections.prescriptionTypeId,
      submission_method: submissionMethod,
      upload_token: state.selections.uploadToken,
      manual_values: state.selections.manualValues,
      email_later: emailLater,
      lens_group_id: itemId(selectedLensGroup),
      lens_id: state.selections.lensId,
      option_selections: state.selections.optionSelections,
      addon_ids: state.selections.addonIds,
      order_note: state.selections.orderNote,
      client_checkpoint: state.currentStep,
    };
  };

  const submitAddToCart = async () => {
    if (state.isAddingToCart) {
      return;
    }

    const config = getConfig();

    setState((current) => ({
      ...current,
      isAddingToCart: true,
      validationError: '',
    }));

    try {
      const response = await fetch(`${config.restUrl ?? ''}add-to-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce ?? '',
        },
        credentials: 'same-origin',
        body: JSON.stringify(buildAddToCartPayload()),
      });

      const payload = (await response.json()) as { cart_url?: string; message?: string };

      if (!response.ok) {
        throw new Error(
          payload.message ||
            t(
              'errors.add_to_cart_failed',
              config.strings?.failedAddToCart ?? 'Unable to add this lens configuration to cart.',
            ),
        );
      }

      if (!payload.cart_url) {
        throw new Error(
          t(
            'errors.add_to_cart_failed',
            config.strings?.failedAddToCart ?? 'Unable to add this lens configuration to cart.',
          ),
        );
      }

      window.location.href = payload.cart_url;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(
              'errors.add_to_cart_failed',
              config.strings?.failedAddToCart ?? 'Unable to add this lens configuration to cart.',
            );

      setState((current) => ({
        ...current,
        isAddingToCart: false,
        validationError: message,
      }));
    }
  };

  const handleNext = () => {
    if (state.currentStep === 'error') {
      closeOverlay();
      return;
    }

    if (state.currentStep === 'review') {
      void submitAddToCart();
      return;
    }

    if (state.currentStep === 'manual') {
      const manualErrors = validateManualValues();
      if (manualErrors.length) {
        setState((current) => ({ ...current, validationError: manualErrors[0] }));
        return;
      }
    }

    if (state.currentStep === 'lens_options') {
      const optionErrors = validateOptionSelections();
      if (optionErrors.length) {
        setState((current) => ({ ...current, validationError: optionErrors[0] }));
        return;
      }
    }

    if (!canContinue()) {
      if (state.currentStep === 'upload' && state.isUploading) {
        setState((current) => ({
          ...current,
          validationError: t('upload.status_uploading', getConfig().strings?.uploading ?? 'Uploading...'),
        }));
      } else if (state.currentStep === 'upload' && state.uploadError) {
        setState((current) => ({ ...current, validationError: current.uploadError }));
      } else {
        setState((current) => ({
          ...current,
          validationError: t('errors.required', 'Please complete this step before continuing.'),
        }));
      }
      return;
    }

    switch (state.currentStep) {
      case 'prescription_type': {
        if (selectedPrescriptionType && !selectedPrescriptionType.requires_submission) {
          const lensEntry = getLensEntryTransition();
          moveToStep(lensEntry.nextStep, (current) => ({
            ...current,
            selections: {
              ...current.selections,
              lensGroupId: lensEntry.lensGroupId || current.selections.lensGroupId,
            },
          }));
          return;
        }
        moveToStep('submission_method');
        return;
      }
      case 'submission_method':
        if (!selectedSubmissionMethod) {
          return;
        }
        moveToStep(itemId(selectedSubmissionMethod) as Step);
        return;
      case 'upload':
      case 'manual':
      case 'email_later': {
        const lensEntry = getLensEntryTransition();
        moveToStep(lensEntry.nextStep, (current) => ({
          ...current,
          selections: {
            ...current.selections,
            lensGroupId: lensEntry.lensGroupId || current.selections.lensGroupId,
          },
        }));
        return;
      }
      case 'lens_group':
        moveToStep('lens');
        return;
      case 'lens':
        moveToStep(getPostLensStepForLens(selectedLens));
        return;
      case 'lens_options':
        moveToStep(availableAddons.length ? 'addons' : 'review');
        return;
      case 'addons':
        moveToStep('review');
        return;
      default:
        return;
    }
  };

  const handleUploadFile = async (file: File | null) => {
    if (!file) {
      return;
    }

    const config = getConfig();
    const maxBytes = (state.uploadRules.max_file_size_mb || 20) * 1024 * 1024;

    setState((current) => ({
      ...current,
      uploadError: '',
      validationError: '',
      isUploading: false,
      uploadMimeType: '',
      uploadFileUrl: '',
      selections: {
        ...current.selections,
        uploadToken: '',
        uploadFileName: file.name,
        uploadSize: file.size,
      },
    }));

    if (file.size > maxBytes) {
      setState((current) => ({
        ...current,
        uploadError: t('errors.upload_too_large', 'The file is larger than 20MB.'),
      }));
      return;
    }

    const formData = new FormData();
    formData.append('product_id', String(state.productId));
    formData.append('file', file, file.name);

    setState((current) => ({
      ...current,
      isUploading: true,
    }));

    try {
      const response = await fetch(`${config.restUrl ?? ''}upload`, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': config.nonce ?? '',
        },
        credentials: 'same-origin',
        body: formData,
      });

      const payload = (await response.json()) as {
        upload_token?: string;
        file_name?: string;
        file_url?: string;
        mime_type?: string;
        size?: number;
        message?: string;
      };

      if (!response.ok) {
        throw new Error(payload.message || t('errors.upload_failed', config.strings?.failedUpload ?? 'Unable to upload your prescription file.'));
      }

      setState((current) => ({
        ...current,
        isUploading: false,
        uploadError: '',
        validationError: '',
        uploadMimeType: payload.mime_type ?? '',
        uploadFileUrl: payload.file_url ?? '',
        selections: {
          ...current.selections,
          uploadToken: payload.upload_token ?? '',
          uploadFileName: payload.file_name ?? current.selections.uploadFileName,
          uploadSize: payload.size ?? current.selections.uploadSize,
        },
      }));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('errors.upload_failed', config.strings?.failedUpload ?? 'Unable to upload your prescription file.');

      setState((current) => ({
        ...current,
        isUploading: false,
        uploadError: message,
        uploadMimeType: '',
        uploadFileUrl: '',
        selections: {
          ...current.selections,
          uploadToken: '',
        },
      }));
    }
  };

  const handleUploadDrop = async (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsUploadDragActive(false);

    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) {
      await handleUploadFile(file);
    }
  };

  const renderOptionalManualUpload = (): React.ReactNode => {
    const uploadTitle = state.isUploading
      ? t('upload.status_uploading', getConfig().strings?.uploading ?? 'Uploading...')
      : state.selections.uploadFileName || t('upload.choose', 'Choose File');
    const uploadMeta = state.uploadError || t('upload.supported', 'Supported: JPG, PNG, WEBP, HEIC, HEIF, PDF up to 20MB');

    return (
      <section className="lens-bird-form-card lens-bird-form-card--upload">
        <h3 className="lens-bird-form-card__title">
          {t(
            'manual.optional_upload_title',
            'Upload your prescription for us to confirm that you have entered it correctly (Optional).',
          )}
        </h3>
        <label className="lens-bird-upload lens-bird-upload--compact">
          <input
            type="file"
            className="lens-bird-upload__input"
            accept={uploadAccept}
            onChange={(event) => void handleUploadFile(event.currentTarget.files?.[0] ?? null)}
          />
          <span className="lens-bird-upload__title">{uploadTitle}</span>
          <span className={state.uploadError ? 'lens-bird-upload__meta is-error' : 'lens-bird-upload__meta'}>{uploadMeta}</span>
        </label>
      </section>
    );
  };

  const getStepMeta = (): StepMeta => {
    const config = getConfig();

    switch (state.currentStep) {
      case 'loading':
        return {
          eyebrow: 'Lens Bird',
          title: t('modal.loading', config.strings?.loading ?? 'Loading lens flow...'),
          lead: '',
          cta: t('modal.close', config.strings?.close ?? 'Close'),
        };
      case 'error':
        return {
          eyebrow: 'Lens Bird',
          title: state.errorMessage || t('errors.invalid_context', config.strings?.failedContext ?? 'Unable to load lens configuration for this product.'),
          lead: '',
          cta: t('actions.finish', config.strings?.finish ?? 'Close'),
        };
      case 'prescription_type':
        return {
          eyebrow: t('summary.current_selection', 'Current Selection'),
          title: t('steps.prescription_type.title', 'Choose Your Prescription Type'),
          lead: t('steps.prescription_type.lead', 'Choose the lens journey that best matches how this frame will be used.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'submission_method':
        return {
          eyebrow: t('summary.prescription_type_label', 'Prescription Type'),
          title: t('steps.submission_method.title', 'Add Your Prescription'),
          lead: t('steps.submission_method.lead', 'Choose how you want to send your prescription for this order.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'upload':
        return {
          eyebrow: t('summary.submission_method_label', 'Submission Method'),
          title: t('steps.upload.title', 'Upload Prescription'),
          lead: t('steps.upload.lead', 'Please upload your prescription as a JPG, PNG, WEBP, HEIC, HEIF, or PDF file.'),
          cta: state.isUploading
            ? t('upload.status_uploading', config.strings?.uploading ?? 'Uploading...')
            : t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'manual':
        return {
          eyebrow: t('summary.submission_method_label', 'Submission Method'),
          title: isReadingPrescriptionType(state.selections.prescriptionTypeId)
            ? t('steps.manual.reading_title', 'Select Your Reading Strength Level')
            : t('steps.manual.title', 'Enter Your Prescription Details'),
          lead: isReadingPrescriptionType(state.selections.prescriptionTypeId)
            ? t('steps.manual.reading_lead', 'Choose the reading power you want for this pair.')
            : t('steps.manual.lead', 'Choose your prescription values for each eye and add PD before continuing.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'email_later':
        return {
          eyebrow: t('summary.submission_method_label', 'Submission Method'),
          title: t('steps.email_later.title', 'Email Later'),
          lead: t('steps.email_later.lead', 'Complete checkout now and send your prescription after the order is placed.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'lens_group':
        return {
          eyebrow: t('summary.lens_group_label', 'Lens Group'),
          title: t('steps.lens_group.title', 'Choose Lens Group'),
          lead: t('steps.lens_group.lead', 'Pick the lens family you want to review for this frame.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'lens':
        return {
          eyebrow: t('summary.lens_label', 'Lens'),
          title: t('steps.lens.title', 'Choose Your Lenses'),
          lead: t('steps.lens.lead', 'Select the lens index and package that best fits this order.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'lens_options':
        return {
          eyebrow: t('summary.options_label', 'Lens Options'),
          title: t('steps.options.title', 'Select Options'),
          lead: t('steps.options.lead', 'Choose the coating and lens options you want to include.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'addons':
        return {
          eyebrow: t('summary.addons_label', 'Add-Ons'),
          title: t('steps.addons.title', 'Add Ons'),
          lead: t('steps.addons.lead', 'Add any optional extras before you review the bundle.'),
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
      case 'review':
        return {
          eyebrow: '',
          title: t('steps.review.title', 'Please Review Your Order'),
          lead: '',
          cta: state.isAddingToCart
            ? t('actions.adding_to_cart', config.strings?.addingToCart ?? 'Adding to cart...')
            : t('actions.add_to_cart', config.strings?.addToCart ?? 'Add to Cart'),
        };
      default:
        return {
          eyebrow: 'Lens Bird',
          title: t('modal.title', 'Lens Bird'),
          lead: '',
          cta: t('actions.next', config.strings?.continue ?? 'Save and Continue'),
        };
    }
  };

  const renderErrorNotice = () => {
    const message = state.validationError || state.uploadError;

    if (!message) {
      return null;
    }

    return <div className="lens-bird-notice lens-bird-notice--error">{message}</div>;
  };

  const renderCurrentStep = (): React.ReactNode => {
    const pricing = calculatePricing();

    switch (state.currentStep) {
      case 'loading':
        return (
          <div className="lens-bird-panel lens-bird-panel--centered">
            <div className="lens-bird-loading">{t('modal.loading', getConfig().strings?.loading ?? 'Loading lens flow...')}</div>
          </div>
        );
      case 'error':
        return (
          <div className="lens-bird-panel lens-bird-panel--centered">
            <div className="lens-bird-notice lens-bird-notice--error">
              {state.errorMessage || t('errors.invalid_context', 'Unable to load lens configuration for this product.')}
            </div>
          </div>
        );
      case 'prescription_type':
        return (
          <div className="lens-bird-panel">
            <SelectionCards
              items={prescriptionTypes}
              selectedId={state.selections.prescriptionTypeId}
              onSelect={selectPrescriptionType}
              formatPriceDelta={formatPriceDelta}
              kind="prescription"
              chooseLabel={chooseLabel}
              selectedLabel={selectedLabel}
              getEyebrow={(item) => prescriptionChoiceHint(item, t)}
            />
          </div>
        );
      case 'submission_method':
        return (
          <div className="lens-bird-panel">
            <SelectionCards
              items={submissionMethods}
              selectedId={state.selections.submissionMethodId}
              onSelect={selectSubmissionMethod}
              formatPriceDelta={formatPriceDelta}
              kind="submission"
              chooseLabel={chooseLabel}
              selectedLabel={selectedLabel}
            />
          </div>
        );
      case 'upload': {
        const uploadActionText = state.isUploading
          ? t('upload.status_uploading', getConfig().strings?.uploading ?? 'Uploading...')
          : state.selections.uploadFileName || t('upload.hero_action', 'Select file');
        const uploadSupportText =
          state.uploadError || t('upload.supported', 'Supported: JPG, PNG, WEBP, HEIC, HEIF, PDF up to 20MB');

        return (
          <div className="lens-bird-panel lens-bird-panel--stack lens-bird-panel--upload-step">
            <label
              className={
                isUploadDragActive
                  ? 'lens-bird-upload lens-bird-upload--hero is-drag-active'
                  : 'lens-bird-upload lens-bird-upload--hero'
              }
              onDragEnter={() => setIsUploadDragActive(true)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsUploadDragActive(true);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setIsUploadDragActive(false);
                }
              }}
              onDrop={(event) => void handleUploadDrop(event)}
            >
              <input
                type="file"
                className="lens-bird-upload__input"
                accept={uploadAccept}
                onChange={(event) => void handleUploadFile(event.currentTarget.files?.[0] ?? null)}
              />
              <span className="lens-bird-upload__icon" aria-hidden="true">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="10" y="38" width="44" height="12" rx="6" stroke="currentColor" strokeWidth="3" />
                  <path d="M32 14V41" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  <path d="M22 24L32 14L42 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="lens-bird-upload__eyebrow">
                {t('upload.tap_label', 'Tap to upload your prescription')}
              </span>
              <span className="lens-bird-upload__hero-title">
                {t('upload.drop_prompt', 'Drop files here or browse your device')}
              </span>
              <span className="lens-bird-upload__hero-subtitle">
                {t(
                  'upload.hero_subtitle',
                  'Upload a clear photo, screenshot, or PDF so we can match your prescription correctly.',
                )}
              </span>
              <span className="lens-bird-upload__hero-action">
                {uploadActionText}
              </span>
              <span className="lens-bird-upload__hero-limit">
                {`(${t('upload.maximum_label', 'maximum')} ${uploadMaxSizeMb} MB)`}
              </span>
              <span className={state.uploadError ? 'lens-bird-upload__hero-formats is-error' : 'lens-bird-upload__hero-formats'}>
                {uploadSupportText}
              </span>
            </label>
            <section className="lens-bird-upload-extra">
              <label className="lens-bird-upload-extra__label" htmlFor="lens-bird-upload-note">
                {t('upload.additional_info_title', 'Additional information')}
              </label>
              <textarea
                id="lens-bird-upload-note"
                className="lens-bird-upload-extra__textarea"
                placeholder={t(
                  'upload.additional_info_placeholder',
                  'Any further instructions regarding your prescription or corrective lens requirements',
                )}
                value={state.selections.orderNote}
                onChange={(event) =>
                  updateSelections((current) => ({
                    ...current,
                    orderNote: event.currentTarget.value,
                  }))
                }
              />
              <p className="lens-bird-upload-extra__note">
                {t(
                  'upload.additional_info_help',
                  'Any extra notes you add here will be attached to this order for review.',
                )}
              </p>
            </section>
          </div>
        );
      }
      case 'manual': {
        const isReadingManual = isReadingPrescriptionType(state.selections.prescriptionTypeId);
        const isProgressiveManual = isProgressivePrescriptionType(state.selections.prescriptionTypeId);
        const usesDualPd = state.selections.manualValues.pd_mode === 'dual';

        if (isReadingManual) {
          return (
            <div className="lens-bird-panel lens-bird-panel--stack">
              <section className="lens-bird-form-card">
                <h3 className="lens-bird-form-card__title">{t('manual.field.diopters', 'Diopters')}</h3>
                <SelectField
                  label={t('manual.field.diopters', 'Diopters')}
                  value={state.selections.manualValues.reading_power}
                  options={READING_DIOPTER_OPTIONS}
                  onChange={(value) => updateManualValue('reading_power', value)}
                />
              </section>
            </div>
          );
        }

        return (
          <div className="lens-bird-panel lens-bird-panel--stack">
            <div className="lens-bird-manual-rows">
              <section className="lens-bird-manual-row">
                <h3 className="lens-bird-manual-row__title">{t('manual.section.od', 'OD (Right Eye)')}</h3>
                <div className={isProgressiveManual ? 'lens-bird-manual-row__grid lens-bird-manual-row__grid--with-add' : 'lens-bird-manual-row__grid'}>
                  <SelectField
                    label={t('manual.field.sphere', 'SPH')}
                    value={state.selections.manualValues.od_sphere}
                    options={SPH_OPTIONS}
                    onChange={(value) => updateManualValue('od_sphere', value)}
                  />
                  <SelectField
                    label={t('manual.field.cylinder', 'CYL')}
                    value={state.selections.manualValues.od_cylinder}
                    options={CYL_OPTIONS}
                    onChange={(value) => updateManualValue('od_cylinder', value)}
                  />
                  <SelectField
                    label={t('manual.field.axis', 'AXIS')}
                    value={state.selections.manualValues.od_axis}
                    options={AXIS_OPTIONS}
                    onChange={(value) => updateManualValue('od_axis', value)}
                  />
                  {isProgressiveManual ? (
                    <SelectField
                      label={t('manual.field.add', 'ADD')}
                      value={state.selections.manualValues.od_add}
                      options={ADD_OPTIONS}
                      onChange={(value) => updateManualValue('od_add', value)}
                    />
                  ) : null}
                </div>
              </section>
              <section className="lens-bird-manual-row">
                <h3 className="lens-bird-manual-row__title">{t('manual.section.os', 'OS (Left Eye)')}</h3>
                <div className={isProgressiveManual ? 'lens-bird-manual-row__grid lens-bird-manual-row__grid--with-add' : 'lens-bird-manual-row__grid'}>
                  <SelectField
                    label={t('manual.field.sphere', 'SPH')}
                    value={state.selections.manualValues.os_sphere}
                    options={SPH_OPTIONS}
                    onChange={(value) => updateManualValue('os_sphere', value)}
                  />
                  <SelectField
                    label={t('manual.field.cylinder', 'CYL')}
                    value={state.selections.manualValues.os_cylinder}
                    options={CYL_OPTIONS}
                    onChange={(value) => updateManualValue('os_cylinder', value)}
                  />
                  <SelectField
                    label={t('manual.field.axis', 'AXIS')}
                    value={state.selections.manualValues.os_axis}
                    options={AXIS_OPTIONS}
                    onChange={(value) => updateManualValue('os_axis', value)}
                  />
                  {isProgressiveManual ? (
                    <SelectField
                      label={t('manual.field.add', 'ADD')}
                      value={state.selections.manualValues.os_add}
                      options={ADD_OPTIONS}
                      onChange={(value) => updateManualValue('os_add', value)}
                    />
                  ) : null}
                </div>
              </section>
            </div>
            <section className="lens-bird-form-card lens-bird-pd-card">
              <h3 className="lens-bird-form-card__title lens-bird-pd-card__title">{t('manual.section.shared', 'Pupillary Distance (PD)')}</h3>
              <div
                className="lens-bird-radio-row lens-bird-pd-card__toggle"
                role="radiogroup"
                aria-label={t('manual.section.shared', 'Pupillary Distance (PD)')}
              >
                <label className="lens-bird-radio lens-bird-pd-card__radio">
                  <input
                    type="radio"
                    name="lens-bird-pd-mode"
                    checked={!usesDualPd}
                    onChange={() => updateManualValue('pd_mode', 'single')}
                  />
                  <span className="lens-bird-radio__control" aria-hidden="true" />
                  <span>{t('manual.toggle.single_pd', 'Single PD')}</span>
                </label>
                <label className="lens-bird-radio lens-bird-pd-card__radio">
                  <input
                    type="radio"
                    name="lens-bird-pd-mode"
                    checked={usesDualPd}
                    onChange={() => updateManualValue('pd_mode', 'dual')}
                  />
                  <span className="lens-bird-radio__control" aria-hidden="true" />
                  <span>{t('manual.toggle.dual_pd', 'Dual PD')}</span>
                </label>
              </div>
              <div
                className={
                  usesDualPd
                    ? 'lens-bird-shared-grid lens-bird-shared-grid--dual lens-bird-pd-card__grid lens-bird-pd-card__grid--dual'
                    : 'lens-bird-shared-grid lens-bird-shared-grid--single lens-bird-pd-card__grid lens-bird-pd-card__grid--single'
                }
              >
                <SelectField
                  label={t(usesDualPd ? 'manual.field.pd_right' : 'manual.field.pd', usesDualPd ? 'Right PD' : 'PD')}
                  value={usesDualPd ? state.selections.manualValues.pd_right : state.selections.manualValues.pd}
                  options={usesDualPd ? DUAL_PD_OPTIONS : PD_OPTIONS}
                  className={usesDualPd ? 'lens-bird-pd-card__field' : 'lens-bird-pd-card__field lens-bird-pd-card__field--single'}
                  hideLabel={!usesDualPd}
                  onChange={(value) => updateManualValue(usesDualPd ? 'pd_right' : 'pd', value)}
                />
                {usesDualPd ? (
                  <SelectField
                    label={t('manual.field.pd_left', 'Left PD')}
                    value={state.selections.manualValues.pd_left}
                    options={DUAL_PD_OPTIONS}
                    className="lens-bird-pd-card__field"
                    onChange={(value) => updateManualValue('pd_left', value)}
                  />
                ) : null}
              </div>
            </section>
            {renderOptionalManualUpload()}
          </div>
        );
      }
      case 'email_later':
        return (
          <div className="lens-bird-panel lens-bird-panel--stack">
            <label className="lens-bird-checkbox">
              <input
                type="checkbox"
                checked={state.selections.emailLaterConfirmed}
                onChange={(event) => {
                  updateSelections((current) => ({
                    ...current,
                    emailLaterConfirmed: event.currentTarget.checked,
                  }));
                  setState((current) => ({ ...current, validationError: '' }));
                }}
              />
              <span>{t('email_later.confirm', 'I understand the prescription will be sent later.')}</span>
            </label>
          </div>
        );
      case 'lens_group':
        return (
          <div className="lens-bird-panel">
            <SelectionCards
              items={lensGroups}
              selectedId={itemId(selectedLensGroup)}
              onSelect={selectLensGroup}
              formatPriceDelta={formatPriceDelta}
              kind="group"
              chooseLabel={chooseLabel}
              selectedLabel={selectedLabel}
            />
          </div>
        );
      case 'lens':
        return (
          <div className="lens-bird-panel">
            <SelectionCards
              items={lenses}
              selectedId={state.selections.lensId}
              onSelect={selectLens}
              showPrice
              formatPriceDelta={formatPriceDelta}
              kind="lens"
              chooseLabel={chooseLabel}
              selectedLabel={selectedLabel}
            />
          </div>
        );
      case 'lens_options':
        return (
          <div className="lens-bird-panel lens-bird-panel--stack">
            {optionGroups.map((group) => (
              <section className="lens-bird-option-group" key={itemId(group)}>
                <div className="lens-bird-option-group__header">
                  <h3 className="lens-bird-option-group__title">{itemLabel(group)}</h3>
                  {group.required ? <span className="lens-bird-option-group__badge">{t('labels.required', 'Required')}</span> : null}
                </div>
                <SelectionCards
                  items={normalizeItems(group.options)}
                  selectedId={state.selections.optionSelections[itemId(group)] ?? ''}
                  onSelect={(optionId) => selectOption(itemId(group), optionId)}
                  showPrice
                  formatPriceDelta={formatPriceDelta}
                  kind="option"
                  chooseLabel={chooseLabel}
                  selectedLabel={selectedLabel}
                />
              </section>
            ))}
          </div>
        );
      case 'addons':
        return (
          <div className="lens-bird-panel">
            <div className="lens-bird-choice-grid">
              {availableAddons.map((addon) => {
                const active = state.selections.addonIds.includes(itemId(addon));

                return (
                  <button
                    key={itemId(addon)}
                    type="button"
                    className={active ? 'lens-bird-choice-card is-selected' : 'lens-bird-choice-card'}
                    data-kind="addon"
                    aria-pressed={active}
                    onClick={() => toggleAddon(itemId(addon))}
                  >
                    <div className="lens-bird-choice-card__badge">{choiceBadge('addon', addon)}</div>
                    <div className="lens-bird-choice-card__content">
                      <div className="lens-bird-choice-card__topline">
                        <div className="lens-bird-choice-card__title">{itemLabel(addon)}</div>
                        <div className="lens-bird-choice-card__price">{formatPriceDelta(asNumber(addon.price_delta))}</div>
                      </div>
                      {splitDescription(addon.description).length ? (
                        <div className="lens-bird-choice-card__desc">{splitDescription(addon.description)[0]}</div>
                      ) : null}
                    </div>
                    <ChoiceCardState selected={active} chooseLabel={chooseLabel} selectedLabel={selectedLabel} />
                  </button>
                );
              })}
            </div>
          </div>
        );
      case 'review':
        return (
          <div className="lens-bird-panel">
            <div className="lens-bird-review-sheet">
              <div className="lens-bird-review-sheet__row">
                <div className="lens-bird-review-sheet__left">
                  <div className="lens-bird-review-sheet__title">{productName}</div>
                </div>
                <div className="lens-bird-review-sheet__price">{formatMoney(pricing.base)}</div>
              </div>
              <div className="lens-bird-review-sheet__divider" />
              <div className="lens-bird-review-sheet__row lens-bird-review-sheet__row--detail">
                <div className="lens-bird-review-sheet__left">
                  <div className="lens-bird-review-sheet__title">{selectedLens ? itemLabel(selectedLens) : '-'}</div>
                  <div className="lens-bird-review-sheet__meta">
                    <div>{`${t('summary.prescription_type_label', 'Prescription Type')} : ${selectedPrescriptionType ? itemLabel(selectedPrescriptionType) : '-'}`}</div>
                    <div>{`${t('summary.submission_method_label', 'Method')} : ${selectedSubmissionMethod ? itemLabel(selectedSubmissionMethod) : t('summary.not_required', 'Not required')}`}</div>
                    {state.selections.emailLaterConfirmed ? <div>{t('summary.pending_email', 'Prescription will be sent later')}</div> : null}
                  </div>
                  {renderManualPrescriptionPreview()}
                  {renderUploadPreview()}
                </div>
                <div className="lens-bird-review-sheet__price">{formatMoney(pricing.lens)}</div>
              </div>
              {getSelectedOptionNames().length ? (
                <>
                  <div className="lens-bird-review-sheet__divider" />
                  <div className="lens-bird-review-sheet__row lens-bird-review-sheet__row--detail">
                    <div className="lens-bird-review-sheet__left">
                      <div className="lens-bird-review-sheet__title">{t('summary.options_label', 'Lens Options')}</div>
                      <div className="lens-bird-review-sheet__meta">
                        <div>{getSelectedOptionNames().join(', ')}</div>
                      </div>
                    </div>
                    <div className="lens-bird-review-sheet__price">{formatMoney(pricing.options)}</div>
                  </div>
                </>
              ) : null}
              {getSelectedAddonNames().length ? (
                <>
                  <div className="lens-bird-review-sheet__divider" />
                  <div className="lens-bird-review-sheet__row lens-bird-review-sheet__row--detail">
                    <div className="lens-bird-review-sheet__left">
                      <div className="lens-bird-review-sheet__title">{t('summary.addons_label', 'Add-Ons')}</div>
                      <div className="lens-bird-review-sheet__meta">
                        <div>{getSelectedAddonNames().join(', ')}</div>
                      </div>
                    </div>
                    <div className="lens-bird-review-sheet__price">{formatMoney(pricing.addons)}</div>
                  </div>
                </>
              ) : null}
              <div className="lens-bird-review-sheet__divider" />
              <div className="lens-bird-review-notes">
                <label className="lens-bird-review-notes__label" htmlFor="lens-bird-order-note">
                  {t('review.order_notes', 'Order Notes')}
                </label>
                <textarea
                  id="lens-bird-order-note"
                  className="lens-bird-review-notes__textarea"
                  value={state.selections.orderNote}
                  onChange={(event) =>
                    updateSelections((current) => ({
                      ...current,
                      orderNote: event.currentTarget.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (!state.isOpen) {
    return null;
  }

  const meta = getStepMeta();
  const pricing = calculatePricing();
  const isReviewStep = state.currentStep === 'review';
  const canGoBack = state.stepHistory.length > 0 && state.currentStep !== 'loading' && state.currentStep !== 'error';
  const showFooter = state.currentStep === 'error' || (state.currentStep !== 'loading' && !AUTO_ADVANCE_STEPS.includes(state.currentStep));
  const showSummaryFooter = showFooter && state.currentStep !== 'error' && !isReviewStep;

  return createPortal(
    <div className="lens-bird-modal is-open">
      <div className="lens-bird-modal__backdrop" onClick={closeOverlay} />
      <div
        className={isDesktopLayout ? 'lens-bird-modal__panel is-desktop' : 'lens-bird-modal__panel is-mobile'}
        role="dialog"
        aria-modal="true"
        aria-label={meta.title}
      >
        {isDesktopLayout ? (
          <aside className="lens-bird-modal__preview">
            <div className="lens-bird-modal__preview-media">
              {productImage ? (
                <img className="lens-bird-modal__preview-image" src={productImage} alt={productName} />
              ) : (
                <div className="lens-bird-modal__preview-fallback">{productName.slice(0, 1) || 'L'}</div>
              )}
            </div>
            <div className="lens-bird-modal__preview-meta">
              <strong className="lens-bird-modal__preview-name">{productName}</strong>
              <span className="lens-bird-modal__preview-price">{formatMoney(pricing.total)}</span>
            </div>
          </aside>
        ) : null}
        <section className="lens-bird-modal__main">
          <div className="lens-bird-modal__header">
            <button
              type="button"
              className={canGoBack ? 'lens-bird-modal__nav lens-bird-modal__nav--back' : 'lens-bird-modal__nav lens-bird-modal__nav--back is-hidden'}
              onClick={goBack}
              aria-label={t('actions.back', getConfig().strings?.back ?? 'Back')}
            >
              <span aria-hidden="true">&lt;</span>
            </button>
            <div className="lens-bird-modal__header-spacer" />
            <button type="button" className="lens-bird-modal__nav lens-bird-modal__nav--close" onClick={closeOverlay} aria-label={t('modal.close', getConfig().strings?.close ?? 'Close')}>
              <span aria-hidden="true">x</span>
            </button>
          </div>
          <div className="lens-bird-modal__body">
            {!isDesktopLayout && (state.currentStep === 'manual' || state.currentStep === 'review') ? (
              <div className="lens-bird-mobile-hero">
                {productImage ? <img className="lens-bird-mobile-hero__image" src={productImage} alt={productName} /> : null}
                <div className="lens-bird-mobile-hero__name">{productName}</div>
              </div>
            ) : null}
            {!isDesktopLayout && state.currentStep !== 'manual' && state.currentStep !== 'review' ? (
              <div className="lens-bird-modal__mobile-product">{productName}</div>
            ) : null}
            {state.currentStep !== 'loading' && state.currentStep !== 'error' ? (
              <div className="lens-bird-step-head">
                {meta.eyebrow ? <div className="lens-bird-step-head__eyebrow">{meta.eyebrow}</div> : null}
                <h2 className="lens-bird-step-head__title">{meta.title}</h2>
                {meta.lead ? <p className="lens-bird-step-head__lead">{meta.lead}</p> : null}
              </div>
            ) : null}
            {renderErrorNotice()}
            {renderCurrentStep()}
          </div>
          {showFooter ? (
            <div className={isReviewStep ? 'lens-bird-modal__footer is-review' : 'lens-bird-modal__footer'}>
              <div className={isReviewStep ? 'lens-bird-modal__footer-actions is-full' : 'lens-bird-modal__footer-actions'}>
                {showSummaryFooter ? (
                  <div className="lens-bird-modal__price-block">
                    <span className="lens-bird-modal__price-label">{t('summary.subtotal', 'Subtotal')}</span>
                    <strong className="lens-bird-modal__price-value">{formatMoney(pricing.total)}</strong>
                  </div>
                ) : null}
                <button
                  type="button"
                  className={isReviewStep ? 'lens-bird-modal__cta is-full' : 'lens-bird-modal__cta'}
                  disabled={state.currentStep === 'loading' || !canContinue()}
                  onClick={handleNext}
                >
                  {meta.cta}
                </button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </div>,
    document.body,
  );
}
